import type { MenuProps, RadioChangeEvent } from "antd";
import {
  Button,
  DatePicker,
  Dropdown,
  Radio,
  Select,
  Space,
  Typography,
} from "antd";
import { useState } from "react";
import DownloadIcon from "../../assets/icons/download.svg";
import { DATE_FILTER_OPTIONS } from "./constant";
import {
  DOWNLOAD_TYPES,
  EXPORT_SELECTION_COLUMNS,
  predefinedDates,
} from "./download.constants";
import { tableFilterService } from "./tableFilter.service";

const { Title } = Typography;

interface DownloadProps {
  filename?: string;
  downloadExportCallback?: (payload?: Record<string, unknown>) => void;
  handlePageSizeChange?: (pageSize: number | string) => void;
  totalCount?: number;
  downloadOptions?: {
    allStartDate?: string;
    isShowFormats?: boolean;
    getFilters?: Record<string, unknown>;
    useService?: () => {
      isPending?: boolean;
      mutateAsync?: (...args: unknown[]) => Promise<unknown>;
    };
  };
}

const Download = ({
  filename = "",
  downloadExportCallback = () => {},
  handlePageSizeChange = () => {},
  totalCount = 0,
  downloadOptions = {},
}: DownloadProps = {}) => {
  const {
    allStartDate = predefinedDates.all.startDate,
    isShowFormats = false,
    getFilters = {},
    useService,
  } = downloadOptions;
  const service = useService?.();
  const [exportSelection, setExportSelection] = useState(
    EXPORT_SELECTION_COLUMNS.ALL_COLUMNS,
  );
  const [downloadType, setDownloadType] = useState<
    (typeof DOWNLOAD_TYPES)[keyof typeof DOWNLOAD_TYPES]
  >(DOWNLOAD_TYPES.CSV);
  const [showRange, setShowRange] = useState(false);
  const [dataDownloadLoading, setDataDownloadLoading] = useState(false);

  const stopPropagation = (event?: React.MouseEvent<HTMLElement>) =>
    event?.stopPropagation();

  const exportColumnSelection = (event: RadioChangeEvent) => {
    setExportSelection(event.target.value);
  };

  const exportDateSelection = (
    value: keyof typeof predefinedDates | "custom",
  ) => {
    setShowRange(value === "custom");

    if (totalCount > 10) {
      handlePageSizeChange(100);
    }

    const payload = predefinedDates[value as keyof typeof predefinedDates];

    if (payload) {
      downloadExportCallback({
        ...payload,
        startDate:
          value === "all" && allStartDate ? allStartDate : payload.startDate,
      });
    }
  };

  const handleCancel = () => {
    downloadExportCallback({ startDate: null, endDate: null });
  };

  const dateExportHandler = async () => {
    const payload = { ...getFilters };
    delete payload.size;
    delete payload.page;

    if (service?.mutateAsync) {
      setDataDownloadLoading(true);
      try {
        const data = await service.mutateAsync({
          payload,
          params: { type: downloadType },
        });

        if (downloadType === DOWNLOAD_TYPES.PDF) {
          tableFilterService.downloadBinaryPdf(data as BlobPart, `${filename}.pdf`);
        } else {
          tableFilterService.downloadCSV(data, `${filename}.csv`);
        }
      } finally {
        setDataDownloadLoading(false);
      }
      return;
    }

    downloadExportCallback({
      ...payload,
      type: downloadType,
      exportSelection,
    });
  };

  const downloadItems: MenuProps["items"] = [
    {
      label: (
        <Title
          level={5}
          className="antd-design-typography-title-level-5"
          onClick={stopPropagation}
        >
          Export Data
        </Title>
      ),
      key: "title",
    },
    {
      label: (
        <div onClick={stopPropagation}>
          <Select
            placeholder="Select Date"
            className="w-full pp-ant-select-selector"
            onChange={exportDateSelection}
            options={DATE_FILTER_OPTIONS}
          />
          {showRange && (
            <>
              <br />
              <DatePicker.RangePicker
                disabledDate={(current) =>
                  tableFilterService.disabledDate(current, allStartDate)
                }
                onChange={(_, values) => {
                  const [startDate, endDate] = values;
                  downloadExportCallback({
                    startDate: startDate ? `${startDate} 00:00:00` : null,
                    endDate: endDate ? `${endDate} 23:59:59` : null,
                  });
                }}
                presets={[]}
              />
            </>
          )}
        </div>
      ),
      key: "date",
    },
    {
      label: <p>Select table to display</p>,
      key: "display-label",
    },
    {
      label: (
        <div onClick={stopPropagation}>
          <h2 className="font-bold ">Columns</h2>
          <Radio.Group
            onChange={exportColumnSelection}
            defaultValue={exportSelection}
            style={{ marginTop: "10px" }}
          >
            <Space direction="vertical" size="middle">
              <Radio value={EXPORT_SELECTION_COLUMNS.ALL_COLUMNS}>
                All columns
              </Radio>
              <Radio value={EXPORT_SELECTION_COLUMNS.VISIBLE_COLUMNS}>
                Visible columns
              </Radio>
            </Space>
          </Radio.Group>
        </div>
      ),
      key: "columns",
    },
    isShowFormats
      ? {
          label: (
            <div onClick={stopPropagation}>
              <h2 className="font-bold ">Download</h2>
              <Radio.Group
                onChange={({ target: { value } }) => setDownloadType(value)}
                defaultValue={downloadType}
                style={{ marginTop: "10px" }}
              >
                <Space direction="vertical" size="middle">
                  <Radio value={DOWNLOAD_TYPES.CSV}>CSV</Radio>
                  <Radio value={DOWNLOAD_TYPES.PDF}>PDF</Radio>
                </Space>
              </Radio.Group>
            </div>
          ),
          key: "formats",
        }
      : null,
    { type: "divider" },
    {
      label: (
        <Space className="flex justify-end " onClick={stopPropagation}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            className="font-bold text-white bg-primary"
            onClick={dateExportHandler}
            loading={dataDownloadLoading || service?.isPending}
          >
            Export
          </Button>
        </Space>
      ),
      key: "actions",
    },
  ];

  return (
    <div className="flex space-x-4 relative top-[3px]">
      <Dropdown menu={{ items: downloadItems }} trigger={["click"]}>
        <button onClick={(event) => event.preventDefault()}>
          <Space>
            <DownloadIcon
              className=" bg-stone-100 px-2 pt-1 pb-[4.2px] rounded h-[30px]"
            />
          </Space>
        </button>
      </Dropdown>
    </div>
  );
};

export default Download;
